<template>
    <div class="ad-card" :dir="direction">
        <!-- Image -->
        <div class="ad-card-image">
            <img v-if="ad.image && ad.image.url" :src="ad.image.url" :alt="ad.title" />
            <div v-else class="ad-card-image-placeholder">
                <i class="material-icons">work</i>
            </div>
        </div>

        <!-- Header bar -->
        <div class="ad-card-header">
            <img src="@/assets/images/rosman-market-new.png" class="ad-card-logo" alt="רוסמן" />
            <span class="ad-card-separator">|</span>
            <span class="ad-card-branch">{{ ad.branch }}</span>
        </div>

        <!-- Body -->
        <div class="ad-card-body">
            <span class="ad-card-badge">{{ ad.employmentType }}</span>
            <h2 class="ad-card-title">{{ ad.title }}</h2>
            <p v-if="ad.description" class="ad-card-description">{{ ad.description }}</p>
        </div>

        <!-- Footer: Apply button (public) -->
        <div v-if="!preview" class="ad-card-footer">
            <button class="ad-card-apply-btn" @click="$emit('apply')">הגש מועמדות</button>
        </div>

        <!-- Footer: Admin controls (preview mode) -->
        <div v-if="preview" class="ad-card-controls">
            <label class="toggle-control">
                <input type="checkbox" :checked="active" @change="$emit('toggle-active')" />
                <span class="toggle-track"></span>
                <span class="toggle-label">{{ active ? 'פעיל' : 'לא פעיל' }}</span>
            </label>
            <button class="ctrl-btn link-btn" @click="$emit('copy-link')" title="העתק לינק">
                <i class="material-icons">content_copy</i>
            </button>
            <button class="ctrl-btn edit-btn" @click="$emit('edit')" title="עריכה">
                <i class="material-icons">edit</i>
            </button>
            <button class="ctrl-btn delete-btn" @click="$emit('delete')" title="מחיקה">
                <i class="material-icons">delete</i>
            </button>
        </div>
    </div>
</template>

<script>
import { computed } from 'vue'

export default {
    name: 'AdCard',
    props: {
        ad: { type: Object, required: true },
        preview: { type: Boolean, default: false },
        adminMode: { type: Boolean, default: false },
        active: { type: Boolean, default: false }
    },
    emits: ['apply', 'toggle-active', 'edit', 'delete', 'copy-link'],
    setup(props) {
        const direction = computed(() => props.ad.language === 'ru' ? 'ltr' : 'rtl')
        return { direction }
    }
}
</script>

<style scoped>
.ad-card {
    background: #ffffff;
    border-radius: 16px;
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.18);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    transition: transform 0.2s, box-shadow 0.2s;
}
.ad-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 36px rgba(0, 0, 0, 0.22);
}

/* Image */
.ad-card-image {
    width: 100%;
    aspect-ratio: 4 / 3;
    overflow: hidden;
    background: #e8ecf0;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}
.ad-card-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}
.ad-card-image-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #b0bec5;
}
.ad-card-image-placeholder i {
    font-size: 64px;
}

/* Header bar */
.ad-card-header {
    background: #FEF200;
    padding: 10px 16px;
    color: #000;
    font-size: 15px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
    border-bottom: 3px solid #000;
}
.ad-card-logo {
    height: 28px;
    width: auto;
    object-fit: contain;
    flex-shrink: 0;
}
.ad-card-separator {
    opacity: 0.35;
    font-size: 16px;
    font-weight: 300;
}
.ad-card-branch {
    font-weight: 700;
    letter-spacing: 0.3px;
    color: #000;
}

/* Body */
.ad-card-body {
    padding: 16px;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 10px;
}

/* Badge */
.ad-card-badge {
    display: inline-block;
    background: #FEF200;
    color: #000;
    border: 1.5px solid #000;
    border-radius: 50px;
    font-size: 14px;
    padding: 4px 16px;
    align-self: flex-start;
    font-weight: 700;
    white-space: nowrap;
}

/* Title */
.ad-card-title {
    font-size: 24px;
    font-weight: 800;
    color: #000;
    margin: 0;
    line-height: 1.25;
}

/* Description */
.ad-card-description {
    font-size: 16px;
    color: #555;
    margin: 0;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    line-height: 1.6;
}

/* Footer */
.ad-card-footer {
    padding: 14px 16px;
    border-top: 2px solid #000;
    flex-shrink: 0;
}
.ad-card-apply-btn {
    width: 100%;
    height: 48px;
    background: #FEF200;
    color: #000;
    border: 2px solid #000;
    border-radius: 50px;
    font-size: 15px;
    font-weight: 800;
    cursor: pointer;
    transition: opacity 0.2s, transform 0.1s, background 0.2s;
    letter-spacing: 0.4px;
    box-shadow: 3px 3px 0px #000;
}
.ad-card-apply-btn:hover {
    background: #ffe600;
    transform: translateY(-2px);
    box-shadow: 4px 5px 0px #000;
}
.ad-card-apply-btn:active {
    transform: translateY(0);
    box-shadow: 1px 1px 0px #000;
}

/* Admin controls */
.ad-card-controls {
    background: #fff;
    border-top: 1px solid #f0f0f0;
    padding: 10px 14px;
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
}
.toggle-control {
    display: flex;
    align-items: center;
    gap: 7px;
    cursor: pointer;
    flex: 1;
}
.toggle-control input {
    display: none;
}
.toggle-track {
    width: 36px;
    height: 20px;
    background: #ccc;
    border-radius: 20px;
    position: relative;
    transition: background 0.25s;
    flex-shrink: 0;
}
.toggle-track::after {
    content: '';
    position: absolute;
    width: 16px;
    height: 16px;
    background: #fff;
    border-radius: 50%;
    top: 2px;
    right: 2px;
    transition: right 0.25s;
    box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}
.toggle-control input:checked + .toggle-track {
    background: #1579F2;
}
.toggle-control input:checked + .toggle-track::after {
    right: 18px;
}
.toggle-label {
    font-size: 12px;
    font-weight: 600;
    color: #555;
}
.ctrl-btn {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    border: 1px solid #e0e0e0;
    background: #fafafa;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s, border-color 0.2s;
}
.ctrl-btn i {
    font-size: 18px;
}
.link-btn:hover {
    background: #EBF4FF;
    border-color: #1579F2;
    color: #1579F2;
}
.edit-btn:hover {
    background: #EBF4FF;
    border-color: #1579F2;
    color: #1579F2;
}
.delete-btn:hover {
    background: #fff0f0;
    border-color: #bb2429;
    color: #bb2429;
}
</style>
